import { useMemo } from 'react';

import type { useContentByLabelDataQuery$variables } from './__generated__/useContentByLabelDataQuery.graphql.ts';

const MAX_RESULTS_CAP = 500;
const DEFAULT_MAX_RESULTS = 15;

export enum ExcerptType {
	None = 'none',
	Simple = 'simple',
	RichContent = 'rich content',
}

/**
 * Sort options supported by the Content By Label macro.
 *
 * The macro param values (left) are mapped to CQL `order by` field names (right).
 * For example, the macro uses `'creation'` but CQL expects `'created'`.
 */
export enum SortOption {
	Creation = 'created',
	Title = 'title',
	Modified = 'modified',
}

const SORT_PARAM_TO_CQL: Record<string, SortOption> = {
	creation: SortOption.Creation,
	title: SortOption.Title,
	modified: SortOption.Modified,
};

function parseSortOption(value: string | undefined): SortOption | undefined {
	if (!value) {
		return undefined;
	}
	return SORT_PARAM_TO_CQL[value];
}

export interface ContentByLabelMacroParams {
	cql?: { value?: string };
	sort?: { value?: string };
	reverse?: { value?: string };
	max?: { value?: string };
	maxResults?: { value?: string };
	title?: { value?: string };
	showLabels?: { value?: string };
	showSpace?: { value?: string };
	excerptType?: { value?: string };
	maxCheckbox?: { value?: string };
}

export interface ParsedContentByLabelParams {
	cql: string;
	sort: SortOption | undefined;
	reverse: boolean;
	max: number;
	maxParseError: boolean;
	title: string;
	showLabels: boolean;
	showSpace: boolean;
	excerptType: ExcerptType;
	limitLabelLinksToSpace: boolean;
	macroParamsForQuery: useContentByLabelDataQuery$variables;
}

function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
	if (value === undefined || value === '') {
		return defaultValue;
	}
	return value === 'true';
}

interface ParseMaxResult {
	max: number;
	maxParseError: boolean;
}

function parseMax(value: string | undefined, maxCheckbox: string | undefined): ParseMaxResult {
	if (maxCheckbox === 'true') {
		return { max: MAX_RESULTS_CAP, maxParseError: false };
	}

	if (value === undefined || value === '') {
		return { max: DEFAULT_MAX_RESULTS, maxParseError: false };
	}

	const parsed = parseInt(value, 10);
	if (isNaN(parsed)) {
		return { max: DEFAULT_MAX_RESULTS, maxParseError: true };
	}

	if (parsed <= 0) {
		return { max: DEFAULT_MAX_RESULTS, maxParseError: false };
	}

	return { max: Math.min(parsed, MAX_RESULTS_CAP), maxParseError: false };
}

function buildOrderByClause(sort: SortOption | undefined, reverse: boolean): string {
	if (!sort) {
		return '';
	}

	const direction = reverse ? 'desc' : 'asc';
	return ` order by ${sort} ${direction}`;
}

function parseExcerptType(value: string | undefined): ExcerptType {
	if (value === ExcerptType.Simple) {
		return ExcerptType.Simple;
	}
	if (value === ExcerptType.RichContent) {
		return ExcerptType.RichContent;
	}
	return ExcerptType.None;
}

/**
 * Builds GraphQL query variables from raw macro parameters.
 *
 * This is a pure function (no hooks) so it can be used both at render time
 * and during SSR query preloading.
 */
export function composeMacroParamsForQuery(
	macroParams: Record<string, { value?: string } | undefined> | undefined,
): useContentByLabelDataQuery$variables {
	const rawCql = macroParams?.cql?.value ?? '';
	const sort = parseSortOption(macroParams?.sort?.value);
	const reverse = parseBoolean(macroParams?.reverse?.value, false);
	const { max } = parseMax(
		macroParams?.max?.value ?? macroParams?.maxResults?.value,
		macroParams?.maxCheckbox?.value,
	);

	const orderByClause = buildOrderByClause(sort, reverse);
	const cql = rawCql + orderByClause;

	const excerptType = parseExcerptType(macroParams?.excerptType?.value);

	return {
		cql,
		first: max,
		includeExcerpt: excerptType === ExcerptType.Simple || excerptType === ExcerptType.RichContent,
		fallbackToBody: excerptType === ExcerptType.RichContent,
	};
}

export function useParsedMacroParams(
	macroParams: ContentByLabelMacroParams | undefined,
): ParsedContentByLabelParams {
	return useMemo(() => {
		const rawCql = macroParams?.cql?.value ?? '';
		const sort = parseSortOption(macroParams?.sort?.value);
		const reverse = parseBoolean(macroParams?.reverse?.value, false);
		const { max, maxParseError } = parseMax(
			macroParams?.max?.value ?? macroParams?.maxResults?.value,
			macroParams?.maxCheckbox?.value,
		);
		const title = macroParams?.title?.value ?? '';
		const showLabels = parseBoolean(macroParams?.showLabels?.value, true);
		const showSpace = parseBoolean(macroParams?.showSpace?.value, true);
		const excerptType = parseExcerptType(macroParams?.excerptType?.value);

		const orderByClause = buildOrderByClause(sort, reverse);
		const cql = rawCql + orderByClause;

		const limitLabelLinksToSpace = cql.includes('space = ');

		const macroParamsForQuery: useContentByLabelDataQuery$variables = {
			cql,
			first: max,
			includeExcerpt: excerptType === ExcerptType.Simple || excerptType === ExcerptType.RichContent,
			fallbackToBody: excerptType === ExcerptType.RichContent,
		};

		return {
			cql,
			sort,
			reverse,
			max,
			maxParseError,
			title,
			showLabels,
			showSpace,
			excerptType,
			limitLabelLinksToSpace,
			macroParamsForQuery,
		};
	}, [macroParams]);
}
